import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { sites as sitesApi, templates as templatesApi, stats as statsApi, submissions as submissionsApi, admin as adminApi, volunteer as volunteerApi } from "@/lib/api";
import GeneratorView from "@/components/GeneratorView";
import ReportView from "@/components/ReportView";
import SubmissionsView from "@/components/SubmissionsView";
import {
  LayoutDashboard,
  MapPin,
  FileText,
  Wand2,
  BarChart3,
  LogOut,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Users,
  Download,
  Trash2,
  TrendingUp,
  UserCheck,
  Shield,
  Plus,
  Search,
  RotateCcw,
} from "lucide-react";

const TABS = [
  { id: "overview",    label: "Overview",    icon: LayoutDashboard },
  { id: "submissions", label: "Submissions", icon: Users },
  { id: "session-mgmt", label: "Session Reset", icon: RotateCcw },
  { id: "site-stats",  label: "Site Stats",  icon: TrendingUp },
  { id: "volunteer-consents", label: "Volunteer Requests", icon: UserCheck },
  { id: "deletion-audit", label: "GDPR Audit Log", icon: Shield },
  { id: "sites",       label: "Sites",       icon: MapPin },
  { id: "templates",   label: "Templates",   icon: FileText },
  { id: "generator",   label: "Generator",   icon: Wand2 },
  { id: "report",      label: "Report",      icon: BarChart3 },
];

export default function Dashboard() {
  const { logout, user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [sitesList, setSitesList] = useState([]);
  const [templatesList, setTemplatesList] = useState([]);
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, st] = await Promise.all([
        sitesApi.list(),
        templatesApi.list(),
        statsApi.get(),
      ]);
      setSitesList(s.data.sites);
      setTemplatesList(t.data.templates);
      setStatsData(st.data);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="dash-layout dark" data-testid="dashboard">
      <aside className="dash-sidebar">
        <div className="dash-logo" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src="/aht_round_orange.png" alt="AHT" style={{ height: 32, width: 32, borderRadius: "50%" }} />
          <span className="dash-logo-text">AHT Admin</span>
        </div>
        <nav className="dash-nav">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`nav-${t.id}`}
              className={`dash-nav-btn ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              <t.icon size={18} />
              <span>{t.label}</span>
            </button>
          ))}
        </nav>
        <div className="dash-sidebar-footer">
          <span className="dash-user">{user}</span>
          <button data-testid="logout-btn" className="dash-logout" onClick={logout}>
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
      <main className="dash-main">
        {loading ? (
          <div className="dash-loading">Loading...</div>
        ) : (
          <>
            {tab === "overview"    && <OverviewTab stats={statsData} />}
            {tab === "submissions" && <SubmissionsTab />}
            {tab === "session-mgmt" && <SessionManagementTab />}
            {tab === "site-stats"  && <SiteStatsTab />}
            {tab === "volunteer-consents" && <VolunteerConsentsTab />}
            {tab === "deletion-audit" && <DeletionAuditTab />}
            {tab === "sites"       && <SitesTab sites={sitesList} onRefresh={fetchAll} />}
            {tab === "templates"   && <TemplatesTab templates={templatesList} onRefresh={fetchAll} />}
            {tab === "generator"   && <GeneratorView sites={sitesList} />}
            {tab === "report"      && <ReportView />}
          </>
        )}
      </main>
    </div>
  );
}

function OverviewTab({ stats }) {
  if (!stats) return null;
  const cards = [
    { label: "Total Sites", value: stats.total_sites, color: "var(--cf-green)" },
    { label: "Within Parish", value: stats.within_parish, color: "var(--cf-teal)" },
    { label: "Outside Parish", value: stats.outside_parish, color: "var(--cf-orange)" },
    { label: "Templates", value: stats.total_templates, color: "var(--cf-blue)" },
  ];
  return (
    <div className="tab-content" data-testid="overview-tab">
      <h2 className="tab-title">Campaign Overview</h2>
      <p className="tab-desc">Buckinghamshire Council Local Plan Sites Survey</p>
      <div className="stats-grid">
        {cards.map((c) => (
          <div key={c.label} className="stat-card" style={{ borderTopColor: c.color }}>
            <span className="stat-value" style={{ color: c.color }}>{c.value}</span>
            <span className="stat-label">{c.label}</span>
          </div>
        ))}
      </div>
      <div className="info-panel">
        <h3>How it works</h3>
        <ol>
          <li>Review the imported <strong>Sites</strong> from the council spreadsheet</li>
          <li>Check the <strong>Templates</strong> — response variants for each form question</li>
          <li>Use the <strong>Generator</strong> to create a bookmarklet with varied responses</li>
          <li>Open the council form, click the bookmarklet to auto-fill each site sequentially</li>
        </ol>
      </div>
    </div>
  );
}

function SitesTab({ sites, onRefresh }) {
  const [reseedLoading, setReseedLoading] = useState(false);
  const [superPassword, setSuperPassword] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { type: 'reseed' | 'delete' | 'add', data?: any }
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSite, setNewSite] = useState({ reference_number: "", site_address: "", postcode: "", area_ha: 0, parish: "within" });
  const [error, setError] = useState("");

  const withinSites = sites.filter((s) => s.parish === "within");
  const outsideSites = sites.filter((s) => s.parish === "outside");

  const requestAction = (actionType, data = null) => {
    setPendingAction({ type: actionType, data });
    setShowPasswordModal(true);
    setError("");
  };

  const executeAction = async () => {
    if (!superPassword) {
      setError("Authorization code required");
      return;
    }
    
    try {
      if (pendingAction.type === "reseed") {
        setReseedLoading(true);
        await sitesApi.reseed(superPassword);
        await onRefresh();
      } else if (pendingAction.type === "delete") {
        await sitesApi.remove(pendingAction.data, superPassword);
        await onRefresh();
      } else if (pendingAction.type === "add") {
        await sitesApi.create(newSite, superPassword);
        setNewSite({ reference_number: "", site_address: "", postcode: "", area_ha: 0, parish: "within" });
        setShowAddForm(false);
        await onRefresh();
      }
      setShowPasswordModal(false);
      setSuperPassword("");
      setPendingAction(null);
      setIsUnlocked(true); // Remember unlock for session
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid authorization code or operation failed");
    } finally {
      setReseedLoading(false);
    }
  };

  return (
    <div className="tab-content" data-testid="sites-tab">
      {/* Password Modal */}
      {showPasswordModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div style={{ background: "#1e293b", padding: 24, borderRadius: 12, width: 400, border: "1px solid #334155" }}>
            <h3 style={{ margin: "0 0 16px", fontSize: "1.1rem" }}>🔐 Authorization Required</h3>
            <p style={{ fontSize: "0.875rem", opacity: 0.8, marginBottom: 16 }}>
              Enter the authorization code to {pendingAction?.type === "reseed" ? "reseed sites" : pendingAction?.type === "delete" ? "delete this site" : "add a new site"}.
            </p>
            <input
              type="password"
              value={superPassword}
              onChange={(e) => setSuperPassword(e.target.value)}
              placeholder="Authorization code"
              style={{
                width: "100%", padding: "10px 12px", background: "#0f172a", border: "1px solid #334155",
                borderRadius: 6, color: "white", marginBottom: 12
              }}
              onKeyDown={(e) => e.key === "Enter" && executeAction()}
              autoFocus
            />
            {error && <p style={{ color: "#ef4444", fontSize: "0.875rem", marginBottom: 12 }}>{error}</p>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => { setShowPasswordModal(false); setSuperPassword(""); setPendingAction(null); setError(""); }}
                style={{ padding: "8px 16px", background: "#334155", border: "none", borderRadius: 6, color: "white", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={executeAction}
                style={{ padding: "8px 16px", background: "#f97316", border: "none", borderRadius: 6, color: "white", cursor: "pointer", fontWeight: 600 }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="tab-header">
        <div>
          <h2 className="tab-title">Sites ({sites.length})</h2>
          <p className="tab-desc">Council consultation sites — protected modifications require authorization</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-secondary" onClick={() => setShowAddForm(!showAddForm)}>
            <Plus size={14} />
            <span>Add Site</span>
          </button>
          <button data-testid="reseed-sites-btn" className="btn-secondary" onClick={() => requestAction("reseed")} disabled={reseedLoading}>
            <RefreshCw size={14} className={reseedLoading ? "spin" : ""} />
            <span>Reseed</span>
          </button>
        </div>
      </div>

      {/* Add Site Form */}
      {showAddForm && (
        <div style={{ background: "#1e293b", padding: 16, borderRadius: 8, marginBottom: 16, border: "1px solid #334155" }}>
          <h4 style={{ margin: "0 0 12px", fontSize: "0.9rem" }}>Add New Site</h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <input
              placeholder="Reference #"
              value={newSite.reference_number}
              onChange={(e) => setNewSite({ ...newSite, reference_number: e.target.value })}
              style={{ padding: 8, background: "#0f172a", border: "1px solid #334155", borderRadius: 4, color: "white" }}
            />
            <input
              placeholder="Site Address"
              value={newSite.site_address}
              onChange={(e) => setNewSite({ ...newSite, site_address: e.target.value })}
              style={{ padding: 8, background: "#0f172a", border: "1px solid #334155", borderRadius: 4, color: "white" }}
            />
            <input
              placeholder="Postcode"
              value={newSite.postcode}
              onChange={(e) => setNewSite({ ...newSite, postcode: e.target.value })}
              style={{ padding: 8, background: "#0f172a", border: "1px solid #334155", borderRadius: 4, color: "white" }}
            />
            <input
              type="number"
              placeholder="Area (Ha)"
              value={newSite.area_ha}
              onChange={(e) => setNewSite({ ...newSite, area_ha: parseFloat(e.target.value) || 0 })}
              style={{ padding: 8, background: "#0f172a", border: "1px solid #334155", borderRadius: 4, color: "white" }}
            />
            <select
              value={newSite.parish}
              onChange={(e) => setNewSite({ ...newSite, parish: e.target.value })}
              style={{ padding: 8, background: "#0f172a", border: "1px solid #334155", borderRadius: 4, color: "white" }}
            >
              <option value="within">Within Parish</option>
              <option value="outside">Outside Parish</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => requestAction("add")}
              disabled={!newSite.reference_number || !newSite.site_address}
              style={{
                padding: "8px 16px", background: "#22c55e", border: "none", borderRadius: 6,
                color: "white", cursor: "pointer", opacity: (!newSite.reference_number || !newSite.site_address) ? 0.5 : 1
              }}
            >
              Add Site
            </button>
            <button
              onClick={() => { setShowAddForm(false); setNewSite({ reference_number: "", site_address: "", postcode: "", area_ha: 0, parish: "within" }); }}
              style={{ padding: "8px 16px", background: "#334155", border: "none", borderRadius: 6, color: "white", cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <SiteGroup title="Within Aylesbury Parish" sites={withinSites} onDelete={(id) => requestAction("delete", id)} />
      <SiteGroup title="Outside Aylesbury Parish" sites={outsideSites} onDelete={(id) => requestAction("delete", id)} />
    </div>
  );
}

function SiteGroup({ title, sites, onDelete }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="site-group">
      <button className="site-group-header" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span>{title} ({sites.length})</span>
      </button>
      {open && (
        <div className="site-table-wrap">
          <table className="site-table" data-testid={`site-table-${title.includes("Within") ? "within" : "outside"}`}>
            <thead>
              <tr>
                <th>Ref</th>
                <th>Address</th>
                <th>Postcode</th>
                <th>Area (Ha)</th>
                <th style={{ width: 60 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((s) => (
                <tr key={s.id}>
                  <td className="mono">{s.reference_number}</td>
                  <td>{s.address || s.site_address}</td>
                  <td className="mono">{s.postcode}</td>
                  <td>{s.area_ha}</td>
                  <td>
                    <button
                      onClick={() => onDelete(s.id)}
                      style={{
                        background: "#fee2e2", color: "#dc2626", border: "none",
                        borderRadius: 4, padding: "4px 8px", cursor: "pointer", fontSize: "0.75rem"
                      }}
                      title="Delete site"
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TemplatesTab({ templates, onRefresh }) {
  const [reseedLoading, setReseedLoading] = useState(false);

  const handleReseed = async () => {
    setReseedLoading(true);
    try {
      await templatesApi.reseed();
      await onRefresh();
    } finally {
      setReseedLoading(false);
    }
  };

  return (
    <div className="tab-content" data-testid="templates-tab">
      <div className="tab-header">
        <div>
          <h2 className="tab-title">Response Templates ({templates.length})</h2>
          <p className="tab-desc">Text variants for each form question — variation engine picks one per site</p>
        </div>
        <button data-testid="reseed-templates-btn" className="btn-secondary" onClick={handleReseed} disabled={reseedLoading}>
          <RefreshCw size={14} className={reseedLoading ? "spin" : ""} />
          <span>Reseed</span>
        </button>
      </div>
      {templates.map((t) => (
        <TemplateCard key={t.id} template={t} />
      ))}
    </div>
  );
}

function TemplateCard({ template }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="template-card">
      <button className="template-card-header" onClick={() => setOpen(!open)}>
        <div>
          <span className="template-key">{template.question_key}</span>
          <span className="template-label">{template.question_label}</span>
        </div>
        <span className="template-count">{template.variants.length} variants</span>
      </button>
      {open && (
        <div className="template-variants">
          {template.variants.map((v, i) => (
            <div key={i} className="template-variant">
              <span className="variant-num">#{i + 1}</span>
              <p>{v}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await submissionsApi.list();
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch submissions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleDelete = async (token) => {
    if (!window.confirm(`Delete submission ${token}? This cannot be undone.`)) return;
    setDeleting(token);
    try {
      await submissionsApi.delete(token);
      await fetchSubmissions();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeleting(null);
    }
  };

  const handleExport = async () => {
    try {
      const res = await submissionsApi.export();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `submissions-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  if (loading) return <div className="dash-loading">Loading submissions...</div>;
  if (!data) return <div className="dash-loading">Failed to load submissions.</div>;

  const { submissions, summary } = data;
  const statusColor = { created: "#9ca3af", in_progress: "#60a5fa", completed: "#4ade80" };
  const statusLabel = { created: "Created", in_progress: "In Progress", completed: "Completed" };

  const summaryCards = [
    { label: "Total Sessions", value: summary.total, color: "var(--cf-blue)" },
    { label: "Completed", value: summary.completed, color: "var(--cf-green)" },
    { label: "With Consent", value: summary.with_consent, color: "var(--cf-teal)" },
    { label: "Completion Rate", value: `${summary.completion_rate}%`, color: "var(--cf-orange)" },
  ];

  return (
    <div className="tab-content" data-testid="submissions-tab">
      <div className="tab-header">
        <div>
          <h2 className="tab-title">Submissions ({summary.total})</h2>
          <p className="tab-desc">GDPR consent tracking and session monitoring</p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button className="btn-secondary" onClick={fetchSubmissions} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spin" : ""} />
            <span>Refresh</span>
          </button>
          <button className="btn-secondary" onClick={handleExport}>
            <Download size={14} />
            <span>Export JSON</span>
          </button>
        </div>
      </div>

      <div className="stats-grid">
        {summaryCards.map((c) => (
          <div key={c.label} className="stat-card" style={{ borderTopColor: c.color }}>
            <span className="stat-value" style={{ color: c.color }}>{c.value}</span>
            <span className="stat-label">{c.label}</span>
          </div>
        ))}
      </div>

      <div className="site-table-wrap" style={{ marginTop: "20px" }}>
        <table className="site-table" data-testid="submissions-table">
          <thead>
            <tr>
              <th>Token</th>
              <th>Name</th>
              <th>Email</th>
              <th>Created</th>
              <th>Status</th>
              <th>Selected</th>
              <th>Submitted</th>
              <th>Consent</th>
              <th>IP</th>
              <th>Sites (refs)</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {submissions.map((s) => (
              <tr key={s.token}>
                <td className="mono" title={s.token}>{s.token?.slice(0, 8)}…</td>
                <td style={{ fontSize: "13px" }}>{s.user_name || "—"}</td>
                <td style={{ fontSize: "12px" }}>{s.user_email || "—"}</td>
                <td style={{ fontSize: "12px" }}>{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td>
                <td>
                  <span
                    className="prefill-badge"
                    style={{
                      backgroundColor: statusColor[s.status] || "#9ca3af",
                      color: "#fff",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                  >
                    {statusLabel[s.status] || s.status || "—"}
                  </span>
                </td>
                <td style={{ textAlign: "center" }}>{s.total_selected ?? s.total ?? 0}</td>
                <td style={{ textAlign: "center" }}>
                  <span style={{ color: s.total_submitted > 0 ? "#4ade80" : "#9ca3af" }}>
                    {s.total_submitted ?? s.completed_count ?? 0}
                  </span>
                </td>
                <td style={{ textAlign: "center" }}>{s.consent_given ? "✓" : "✗"}</td>
                <td className="mono" style={{ fontSize: "11px" }}>{s.client_ip || "—"}</td>
                <td style={{ fontSize: "11px", maxWidth: "160px" }}>
                  <span title={(s.selected_site_refs || []).join(", ")}>
                    {(s.selected_site_refs || []).slice(0, 4).join(", ")}
                    {(s.selected_site_refs || []).length > 4 && ` +${(s.selected_site_refs || []).length - 4}`}
                  </span>
                </td>
                <td>
                  <button
                    className="btn-secondary btn-sm"
                    onClick={() => handleDelete(s.token)}
                    disabled={deleting === s.token}
                    title="Delete submission (GDPR right to erasure)"
                    style={{ padding: "4px 8px" }}
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
            {submissions.length === 0 && (
              <tr>
                <td colSpan={11} style={{ textAlign: "center", padding: "24px", color: "#9ca3af" }}>
                  No submissions yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Site Stats Tab — per-site selected vs submitted counts
// ─────────────────────────────────────────────────────────────
function SiteStatsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("selected_count"); // selected_count | submitted_count | completion_rate | reference_number

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.siteStats();
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch site stats:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return <div className="dash-loading">Loading site stats…</div>;
  if (!data) return <div className="dash-loading">Failed to load site stats.</div>;

  const { site_stats, total_sessions } = data;

  // Sort
  const sorted = [...site_stats].sort((a, b) => {
    if (sortBy === "reference_number") return a.reference_number.localeCompare(b.reference_number);
    return (b[sortBy] ?? 0) - (a[sortBy] ?? 0);
  });

  // Summary totals
  const totalSelected  = site_stats.reduce((s, r) => s + r.selected_count, 0);
  const totalSubmitted = site_stats.reduce((s, r) => s + r.submitted_count, 0);
  const avgRate = site_stats.length
    ? Math.round(site_stats.reduce((s, r) => s + r.completion_rate, 0) / site_stats.length)
    : 0;

  const summaryCards = [
    { label: "Total Sessions",      value: total_sessions,  color: "var(--cf-blue)" },
    { label: "Total Selections",    value: totalSelected,   color: "var(--cf-teal)" },
    { label: "Total Submissions",   value: totalSubmitted,  color: "var(--cf-green)" },
    { label: "Avg Completion Rate", value: `${avgRate}%`,   color: "var(--cf-orange)" },
  ];

  const SortBtn = ({ field, label }) => (
    <button
      onClick={() => setSortBy(field)}
      style={{
        background: "none", border: "none", cursor: "pointer",
        color: sortBy === field ? "#60a5fa" : "#9ca3af",
        fontWeight: sortBy === field ? 700 : 400,
        fontSize: "13px", padding: 0,
      }}
    >
      {label}{sortBy === field ? " ↓" : ""}
    </button>
  );

  return (
    <div className="tab-content" data-testid="site-stats-tab">
      <div className="tab-header">
        <div>
          <h2 className="tab-title">Site Stats</h2>
          <p className="tab-desc">How many supporters selected and submitted responses for each site</p>
        </div>
        <button className="btn-secondary" onClick={fetchStats} disabled={loading}>
          <RefreshCw size={14} className={loading ? "spin" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      <div className="stats-grid">
        {summaryCards.map((c) => (
          <div key={c.label} className="stat-card" style={{ borderTopColor: c.color }}>
            <span className="stat-value" style={{ color: c.color }}>{c.value}</span>
            <span className="stat-label">{c.label}</span>
          </div>
        ))}
      </div>

      <div className="site-table-wrap" style={{ marginTop: "20px" }}>
        <table className="site-table" data-testid="site-stats-table">
          <thead>
            <tr>
              <th><SortBtn field="reference_number" label="Ref" /></th>
              <th>Address</th>
              <th>PC</th>
              <th>Ha</th>
              <th><SortBtn field="selected_count" label="Selected" /></th>
              <th><SortBtn field="submitted_count" label="Submitted" /></th>
              <th><SortBtn field="completion_rate" label="Rate" /></th>
              <th>Progress</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => (
              <tr key={s.reference_number}>
                <td className="mono">{s.reference_number}</td>
                <td style={{ fontSize: "12px", maxWidth: "220px" }}>{s.address}</td>
                <td className="mono" style={{ fontSize: "11px" }}>{s.postcode}</td>
                <td style={{ fontSize: "12px" }}>{s.area_ha}</td>
                <td style={{ textAlign: "center", fontWeight: 600 }}>
                  {s.selected_count}
                </td>
                <td style={{ textAlign: "center", color: s.submitted_count > 0 ? "#4ade80" : "#9ca3af", fontWeight: 600 }}>
                  {s.submitted_count}
                </td>
                <td style={{ textAlign: "center" }}>
                  <span style={{
                    color: s.completion_rate >= 80 ? "#4ade80" : s.completion_rate >= 40 ? "#fbbf24" : "#9ca3af",
                    fontWeight: 600,
                  }}>
                    {s.completion_rate}%
                  </span>
                </td>
                <td style={{ minWidth: "80px" }}>
                  <div style={{
                    background: "rgba(255,255,255,.1)", borderRadius: "4px", height: "6px", width: "100%",
                  }}>
                    <div style={{
                      background: s.completion_rate >= 80 ? "#4ade80" : s.completion_rate >= 40 ? "#fbbf24" : "#60a5fa",
                      height: "100%", borderRadius: "4px",
                      width: `${Math.min(s.completion_rate, 100)}%`,
                      transition: "width .3s",
                    }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// SESSION MANAGEMENT TAB — Search and reset user sessions
// ============================================================
function SessionManagementTab() {
  const [searchEmail, setSearchEmail] = useState("");
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [resetModal, setResetModal] = useState(null); // session to reset
  const [resetReason, setResetReason] = useState("");
  const [resetting, setResetting] = useState(false);
  const [auditRecords, setAuditRecords] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [loadingAudit, setLoadingAudit] = useState(false);

  const handleSearch = async () => {
    if (searchEmail.length < 3) {
      alert("Please enter at least 3 characters to search");
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await adminApi.searchSessions(searchEmail);
      setSessions(res.data.sessions || []);
    } catch (err) {
      console.error("Search failed:", err);
      alert(err.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!resetModal) return;
    setResetting(true);
    try {
      await adminApi.resetSession(resetModal.token, resetReason);
      setResetModal(null);
      setResetReason("");
      // Refresh search results
      handleSearch();
      alert("Session reset successfully! The user can now start fresh.");
    } catch (err) {
      console.error("Reset failed:", err);
      alert(err.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const fetchAudit = async () => {
    setLoadingAudit(true);
    try {
      const res = await adminApi.getSessionResetAudit();
      setAuditRecords(res.data.audit_records || []);
    } catch (err) {
      console.error("Failed to fetch audit:", err);
    } finally {
      setLoadingAudit(false);
    }
  };

  const handleFixStatus = async () => {
    if (!window.confirm("This will fix any sessions with 'confirmed_incomplete' status by updating them to 'done'. Continue?")) return;
    try {
      const res = await adminApi.fixConfirmedIncomplete();
      alert(`Fixed ${res.data.modified_count} sessions`);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to fix statuses");
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return "-";
    return new Date(isoStr).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  const statusColor = { created: "#9ca3af", in_progress: "#60a5fa", completed: "#4ade80", done: "#22c55e" };
  const statusLabel = { created: "Created", in_progress: "In Progress", completed: "Completed", done: "Done" };

  return (
    <div className="tab-content" data-testid="session-mgmt-tab">
      {/* Reset Modal */}
      {resetModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div style={{ background: "#1e293b", padding: 24, borderRadius: 12, width: 480, border: "1px solid #334155" }}>
            <h3 style={{ margin: "0 0 16px", fontSize: "1.1rem", color: "#f97316" }}>
              <RotateCcw size={18} style={{ marginRight: 8, verticalAlign: "middle" }} />
              Reset Session
            </h3>
            <div style={{ background: "rgba(249, 115, 22, 0.1)", padding: 12, borderRadius: 8, marginBottom: 16, border: "1px solid rgba(249, 115, 22, 0.2)" }}>
              <div style={{ fontSize: "0.875rem", marginBottom: 8 }}>
                <strong>User:</strong> {resetModal.user_name || "Unknown"} ({resetModal.user_email})
              </div>
              <div style={{ fontSize: "0.875rem" }}>
                <strong>Current Status:</strong> {statusLabel[resetModal.status] || resetModal.status}
              </div>
            </div>
            <p style={{ fontSize: "0.875rem", opacity: 0.9, marginBottom: 16 }}>
              This will reset the session to its initial state, allowing the user to restart the submission process. 
              The current progress will be archived for audit purposes.
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: "0.875rem", marginBottom: 6, opacity: 0.8 }}>
                Reason for reset (optional but recommended):
              </label>
              <textarea
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                placeholder="e.g., User reported incomplete submission, technical issue..."
                style={{
                  width: "100%", padding: "10px 12px", background: "#0f172a", border: "1px solid #334155",
                  borderRadius: 6, color: "white", minHeight: 80, resize: "vertical"
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => { setResetModal(null); setResetReason(""); }}
                style={{ padding: "8px 16px", background: "#334155", border: "none", borderRadius: 6, color: "white", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                disabled={resetting}
                style={{ 
                  padding: "8px 16px", background: "#f97316", border: "none", borderRadius: 6, 
                  color: "white", cursor: "pointer", fontWeight: 600, opacity: resetting ? 0.6 : 1
                }}
              >
                {resetting ? "Resetting..." : "Confirm Reset"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="tab-header">
        <div>
          <h2 className="tab-title">Session Management</h2>
          <p className="tab-desc">Search and reset user sessions to allow them to redo the submission process</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button 
            className="btn-secondary" 
            onClick={handleFixStatus}
            title="Fix any sessions stuck with 'confirmed_incomplete' status"
          >
            <RefreshCw size={14} />
            <span>Fix Status Issues</span>
          </button>
          <button 
            className="btn-secondary" 
            onClick={() => { setShowAudit(!showAudit); if (!showAudit) fetchAudit(); }}
          >
            <Shield size={14} />
            <span>{showAudit ? "Hide" : "Show"} Reset Audit Log</span>
          </button>
        </div>
      </div>

      {/* Search Section */}
      <div style={{ 
        background: "#1e293b", padding: 20, borderRadius: 12, marginBottom: 24, 
        border: "1px solid #334155"
      }}>
        <h3 style={{ margin: "0 0 12px", fontSize: "1rem", fontWeight: 600 }}>
          <Search size={16} style={{ marginRight: 8, verticalAlign: "middle" }} />
          Search Sessions by Email
        </h3>
        <div style={{ display: "flex", gap: 12 }}>
          <input
            type="text"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            placeholder="Enter user's email address..."
            style={{
              flex: 1, padding: "10px 14px", background: "#0f172a", border: "1px solid #334155",
              borderRadius: 6, color: "white", fontSize: "0.9rem"
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            onClick={handleSearch}
            disabled={loading || searchEmail.length < 3}
            style={{
              padding: "10px 20px", background: "#3b82f6", border: "none", borderRadius: 6,
              color: "white", cursor: "pointer", fontWeight: 600, 
              opacity: (loading || searchEmail.length < 3) ? 0.5 : 1,
              display: "flex", alignItems: "center", gap: 8
            }}
          >
            <Search size={16} />
            {loading ? "Searching..." : "Search"}
          </button>
        </div>
      </div>

      {/* Search Results */}
      {searched && (
        <div className="site-table-wrap">
          <table className="site-table" data-testid="session-search-results">
            <thead>
              <tr>
                <th>Token</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Created</th>
                <th>Status</th>
                <th>Selected</th>
                <th>Submitted</th>
                <th>Consent</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.token}>
                  <td className="mono" title={s.token}>{s.token?.slice(0, 8)}…</td>
                  <td style={{ fontSize: "13px" }}>{s.user_name || "—"}</td>
                  <td style={{ fontSize: "12px" }}>{s.user_email || "—"}</td>
                  <td style={{ fontSize: "12px" }}>{s.user_phone || "—"}</td>
                  <td style={{ fontSize: "12px" }}>{formatDate(s.created_at)}</td>
                  <td>
                    <span
                      style={{
                        backgroundColor: statusColor[s.status] || "#9ca3af",
                        color: "#fff",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      {statusLabel[s.status] || s.status || "—"}
                    </span>
                  </td>
                  <td style={{ textAlign: "center" }}>{s.total_selected ?? 0}</td>
                  <td style={{ textAlign: "center", color: s.total_submitted > 0 ? "#4ade80" : "#9ca3af" }}>
                    {s.total_submitted ?? 0}
                  </td>
                  <td style={{ textAlign: "center" }}>{s.consent_given ? "✓" : "✗"}</td>
                  <td>
                    <button
                      onClick={() => setResetModal(s)}
                      style={{
                        background: "#f97316", color: "white", border: "none",
                        borderRadius: 4, padding: "6px 12px", cursor: "pointer", fontSize: "0.75rem",
                        display: "flex", alignItems: "center", gap: 4
                      }}
                      title="Reset this session"
                    >
                      <RotateCcw size={12} /> Reset
                    </button>
                  </td>
                </tr>
              ))}
              {sessions.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center", padding: "24px", color: "#9ca3af" }}>
                    No sessions found for "{searchEmail}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Audit Log Section */}
      {showAudit && (
        <div style={{ marginTop: 32 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>
              <Shield size={16} style={{ marginRight: 8, verticalAlign: "middle" }} />
              Session Reset Audit Log
            </h3>
            <button className="btn-secondary" onClick={fetchAudit} disabled={loadingAudit}>
              <RefreshCw size={14} className={loadingAudit ? "spin" : ""} />
              <span>Refresh</span>
            </button>
          </div>
          
          {loadingAudit ? (
            <div className="dash-loading">Loading audit log...</div>
          ) : (
            <div className="site-table-wrap">
              <table className="site-table" data-testid="reset-audit-table">
                <thead>
                  <tr>
                    <th>Audit ID</th>
                    <th>Reset At</th>
                    <th>Reset By</th>
                    <th>User Email</th>
                    <th>Previous Status</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {auditRecords.map((r) => (
                    <tr key={r.audit_id}>
                      <td className="mono" style={{ fontSize: "0.7rem" }}>{r.audit_id?.slice(0, 8)}…</td>
                      <td style={{ fontSize: "12px" }}>{formatDate(r.reset_at)}</td>
                      <td style={{ fontWeight: 600 }}>{r.reset_by}</td>
                      <td style={{ fontSize: "12px" }}>{r.original_state?.user_email || "—"}</td>
                      <td>
                        <span style={{
                          backgroundColor: statusColor[r.original_state?.status] || "#9ca3af",
                          color: "#fff", padding: "2px 8px", borderRadius: "4px", fontSize: "11px",
                        }}>
                          {statusLabel[r.original_state?.status] || r.original_state?.status || "—"}
                        </span>
                      </td>
                      <td style={{ fontSize: "12px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.reset_reason || "—"}
                      </td>
                    </tr>
                  ))}
                  {auditRecords.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center", padding: "24px", color: "#9ca3af" }}>
                        No reset records yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// VOLUNTEER CONSENTS TAB — Admin view of volunteer requests
// ============================================================
const STATUS_COLORS = {
  pending: { bg: "#fef3c7", color: "#92400e" },
  contacted: { bg: "#dbeafe", color: "#1e40af" },
  in_progress: { bg: "#ede9fe", color: "#5b21b6" },
  submitted: { bg: "#d1fae5", color: "#065f46" },
  cancelled: { bg: "#fee2e2", color: "#991b1b" },
};

function VolunteerConsentsTab() {
  const [consents, setConsents] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const fetchConsents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await volunteerApi.adminListConsents();
      setConsents(res.data.consents || []);
      setByStatus(res.data.by_status || {});
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConsents();
  }, [fetchConsents]);

  const handleDelete = async (recordId) => {
    if (!window.confirm("Are you sure you want to permanently delete this record?")) return;
    try {
      await volunteerApi.deleteConsent(recordId);
      fetchConsents();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await volunteerApi.updateConsent(recordId, { status: newStatus });
      fetchConsents();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleCancelDeletion = async (recordId) => {
    try {
      await volunteerApi.cancelDeletion(recordId);
      fetchConsents();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to cancel deletion");
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return "-";
    return new Date(isoStr).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) return <div className="dash-loading">Loading volunteer consents...</div>;

  return (
    <div className="dash-tab" data-testid="volunteer-consents-tab">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>Volunteer Consent Requests</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={fetchConsents} className="dash-btn-secondary">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Status summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 24 }}>
        {Object.entries(byStatus).map(([status, count]) => (
          <div key={status} style={{
            background: STATUS_COLORS[status]?.bg || "#f3f4f6",
            color: STATUS_COLORS[status]?.color || "#374151",
            padding: "12px 16px",
            borderRadius: 8,
          }}>
            <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>{count}</div>
            <div style={{ fontSize: "0.75rem", textTransform: "capitalize" }}>{status.replace("_", " ")}</div>
          </div>
        ))}
      </div>

      {/* Consents table */}
      <div className="dash-card" style={{ overflow: "auto" }}>
        <table className="dash-table" data-testid="volunteer-consents-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Postcode</th>
              <th>Status</th>
              <th>Assigned</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {consents.map((c) => (
              <>
                <tr key={c.id} onClick={() => setExpandedId(expandedId === c.id ? null : c.id)} style={{ cursor: "pointer" }}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {c.deletion_recommended && (
                        <span title={`🔒 Locked - Deletion requested by ${c.deletion_recommended_by}: ${c.deletion_reason}`} style={{ color: "#ef4444" }}>🔒</span>
                      )}
                      {c.name || <span style={{ opacity: 0.5 }}>-</span>}
                    </div>
                  </td>
                  <td>{c.email || "-"}</td>
                  <td>{c.mobile || "-"}</td>
                  <td>{c.postcode || "-"}</td>
                  <td>
                    {c.deletion_recommended ? (
                      <span style={{
                        background: "#fee2e2",
                        color: "#991b1b",
                        padding: "4px 8px",
                        borderRadius: 4,
                        fontSize: "0.75rem",
                      }}>
                        🔒 Locked
                      </span>
                    ) : (
                      <select
                        value={c.status || "pending"}
                        onChange={(e) => { e.stopPropagation(); handleStatusChange(c.id, e.target.value); }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          background: STATUS_COLORS[c.status]?.bg || "#f3f4f6",
                          color: STATUS_COLORS[c.status]?.color || "#374151",
                          border: "none",
                          borderRadius: 4,
                          padding: "4px 8px",
                          fontSize: "0.75rem",
                          cursor: "pointer",
                        }}
                      >
                        {["pending", "contacted", "in_progress", "submitted", "cancelled"].map((s) => (
                          <option key={s} value={s}>{s.replace("_", " ")}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td style={{ fontSize: "0.75rem" }}>{c.assigned_to || "-"}</td>
                  <td style={{ fontSize: "0.75rem" }}>{formatDate(c.created_at)}</td>
                  <td>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                      style={{
                        background: "#fee2e2",
                        color: "#dc2626",
                        border: "none",
                        borderRadius: 4,
                        padding: "4px 8px",
                        fontSize: "0.75rem",
                        cursor: "pointer",
                      }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
                {expandedId === c.id && (
                  <tr key={`${c.id}-details`}>
                    <td colSpan={8} style={{ background: "rgba(0,0,0,.2)", padding: 16 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <div>
                          <div style={{ fontSize: "0.75rem", opacity: 0.7, marginBottom: 4 }}>Notes</div>
                          <div style={{ fontSize: "0.875rem" }}>{c.notes || <span style={{ opacity: 0.5 }}>No notes</span>}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: "0.75rem", opacity: 0.7, marginBottom: 4 }}>Consent Text</div>
                          <div style={{ fontSize: "0.75rem", opacity: 0.8 }}>{c.consent_text?.substring(0, 100)}...</div>
                        </div>
                        {c.deletion_recommended && (
                          <div style={{ gridColumn: "span 2", background: "rgba(239,68,68,.1)", padding: 12, borderRadius: 8 }}>
                            <div style={{ color: "#ef4444", fontWeight: 600, fontSize: "0.875rem", marginBottom: 4 }}>
                              🔒 Record Locked - Deletion Recommended by {c.deletion_recommended_by}
                            </div>
                            <div style={{ fontSize: "0.875rem" }}>Reason: {c.deletion_reason || "No reason provided"}</div>
                            <div style={{ fontSize: "0.75rem", opacity: 0.7, marginBottom: 8 }}>Requested: {formatDate(c.deletion_recommended_at)}</div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                                style={{
                                  background: "#dc2626",
                                  color: "white",
                                  border: "none",
                                  borderRadius: 4,
                                  padding: "6px 12px",
                                  fontSize: "0.75rem",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <Trash2 size={12} /> Confirm Delete
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleCancelDeletion(c.id); }}
                                style={{
                                  background: "#374151",
                                  color: "white",
                                  border: "none",
                                  borderRadius: 4,
                                  padding: "6px 12px",
                                  fontSize: "0.75rem",
                                  cursor: "pointer",
                                }}
                              >
                                Cancel Deletion & Unlock
                              </button>
                            </div>
                          </div>
                        )}
                        {c.action_log && c.action_log.length > 0 && (
                          <div style={{ gridColumn: "span 2" }}>
                            <div style={{ fontSize: "0.75rem", opacity: 0.7, marginBottom: 8 }}>Activity Log</div>
                            <div style={{ fontSize: "0.75rem", display: "flex", flexDirection: "column", gap: 4 }}>
                              {c.action_log.slice(-5).map((log, i) => (
                                <div key={i} style={{ opacity: 0.8 }}>
                                  {formatDate(log.timestamp)} — <strong>{log.by}</strong>
                                  {log.status_change && <span style={{ color: "#60a5fa" }}> {log.status_change}</span>}
                                  {log.action && <span style={{ color: "#4ade80" }}> {log.action}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
        {consents.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", opacity: 0.6 }}>No volunteer consent requests yet</div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// DELETION AUDIT LOG TAB — GDPR compliance
// ============================================================
function DeletionAuditTab() {
  const [auditRecords, setAuditRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await volunteerApi.getDeletionAuditLog();
      setAuditRecords(res.data.audit_records || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  const formatDate = (isoStr) => {
    if (!isoStr) return "-";
    return new Date(isoStr).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) return <div className="dash-loading">Loading audit log...</div>;

  return (
    <div className="dash-tab" data-testid="deletion-audit-tab">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>GDPR Deletion Audit Log</h2>
          <p style={{ margin: "4px 0 0", fontSize: "0.875rem", opacity: 0.7 }}>
            Permanent record of all deleted volunteer consent records for compliance auditing.
          </p>
        </div>
        <button onClick={fetchAuditLog} className="dash-btn-secondary">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ marginBottom: 24, padding: 16, background: "rgba(59, 130, 246, 0.1)", borderRadius: 8, border: "1px solid rgba(59, 130, 246, 0.2)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Shield size={18} style={{ color: "#3b82f6" }} />
          <span style={{ fontWeight: 600 }}>Audit Summary</span>
        </div>
        <div style={{ fontSize: "0.875rem" }}>
          Total deletions recorded: <strong>{auditRecords.length}</strong>
        </div>
      </div>

      {/* Audit records table */}
      <div className="dash-card" style={{ overflow: "auto" }}>
        <table className="dash-table" data-testid="audit-log-table">
          <thead>
            <tr>
              <th>Audit ID</th>
              <th>Deleted At</th>
              <th>Deleted By</th>
              <th>Original Name</th>
              <th>Original Email</th>
              <th>Reason</th>
              <th>Recommended By</th>
            </tr>
          </thead>
          <tbody>
            {auditRecords.map((r) => (
              <>
                <tr key={r.audit_id} onClick={() => setExpandedId(expandedId === r.audit_id ? null : r.audit_id)} style={{ cursor: "pointer" }}>
                  <td style={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{r.audit_id}</td>
                  <td style={{ fontSize: "0.75rem" }}>{formatDate(r.deleted_at)}</td>
                  <td style={{ fontWeight: 600 }}>{r.deleted_by}</td>
                  <td>{r.original_name || "-"}</td>
                  <td>{r.original_email || "-"}</td>
                  <td style={{ fontSize: "0.75rem", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.deletion_reason || "-"}
                  </td>
                  <td style={{ fontSize: "0.75rem" }}>{r.deletion_recommended_by || "Direct admin"}</td>
                </tr>
                {expandedId === r.audit_id && (
                  <tr key={`${r.audit_id}-details`}>
                    <td colSpan={7} style={{ background: "rgba(0,0,0,.2)", padding: 16 }}>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: "0.75rem", opacity: 0.7, marginBottom: 4 }}>Full Original Record (Archived)</div>
                        <pre style={{ 
                          background: "rgba(0,0,0,.3)", 
                          padding: 12, 
                          borderRadius: 8, 
                          fontSize: "0.7rem",
                          overflow: "auto",
                          maxHeight: 300,
                        }}>
                          {JSON.stringify(r.original_record, null, 2)}
                        </pre>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, fontSize: "0.75rem" }}>
                        <div>
                          <span style={{ opacity: 0.7 }}>Record Created:</span>{" "}
                          {formatDate(r.record_created_at)}
                        </div>
                        <div>
                          <span style={{ opacity: 0.7 }}>Deletion Requested:</span>{" "}
                          {formatDate(r.deletion_recommended_at)}
                        </div>
                        <div>
                          <span style={{ opacity: 0.7 }}>Deletion Executed:</span>{" "}
                          {formatDate(r.deleted_at)}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
        {auditRecords.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", opacity: 0.6 }}>
            No deletion records yet. When volunteer consent records are deleted, they will be archived here for GDPR compliance.
          </div>
        )}
      </div>
    </div>
  );
}
